import { useEffect, useMemo, useState } from "react"

import { Col, Row } from "react-bootstrap"
import { BehaviorSubject, concat, of } from "rxjs"
import { catchError, distinctUntilChanged, map, switchMap } from "rxjs/operators"

import PendingDrugTestRow from "./PendingDrugTestRow"
import ApiResult from "../../../api/ApiResult"
import OrphanedRow from "../../../api/OrphanedResults/Models/OrphanedRow"
import PendingDrugTest from "../../../api/OrphanedResults/Models/PendingDrugTest"
import PendingDrugTestGridData from "../../../api/OrphanedResults/Models/PendingDrugTestGridData"
import PendingDrugTestOptions from "../../../api/OrphanedResults/Models/PendingDrugTestOptions"
import { GetPendingDrugTestsObservable } from "../../../api/OrphanedResults/ResultExceptionsProcess"
import handleAPIError from "../../../utils/handleAPIError"
import Checkbox from "../../Checkbox"
import LoadingTableBodyWrapper from "../../LoadingTableBodyWrapper"
import LoadingTableWrapper from "../../LoadingTableWrapper"
import TablePagination from "../../TablePagination"

const PageSize = 5
interface PendingDrugTestsProps {
  donorId: number
  importTest: OrphanedRow
  selectedTest?: PendingDrugTest
  showTitle?: boolean
  onSelectTest: (test: PendingDrugTest | undefined) => void
}

function PendingDrugTests({
  donorId,
  importTest,
  selectedTest = undefined,
  showTitle = true,
  onSelectTest,
}: PendingDrugTestsProps) {
  const [gridData, setGridData] = useState<PendingDrugTestGridData>({ Records: [], Pages: 0, Total: 0 })
  const [loadingTests, setLoadingTests] = useState<boolean>(false)

  const page$ = useMemo(
    () =>
      new BehaviorSubject<PendingDrugTestOptions>({
        DonorID: donorId,
        TestTypeID: importTest.TestTypeID,
        SampleTypeCode: importTest.SampleTypeCode,
        PageNumber: 1,
        PageSize,
      }),
    [donorId, importTest.TestTypeID, importTest.SampleTypeCode]
  )

  useEffect(() => {
    const subscription = page$
      .pipe(
        distinctUntilChanged(),
        switchMap((params) =>
          concat(
            of({ loading: true }),
            GetPendingDrugTestsObservable(params).pipe(
              map((result) => ({ loading: false, result })),
              catchError((error) => of({ loading: false, result: undefined, error }))
            )
          )
        )
      )
      .subscribe(
        ({ loading, result, error }: ApiResult<PendingDrugTestGridData>) => {
          setLoadingTests(loading)

          if (!loading) {
            const parsedRecords = result?.Records.map((test) => ({
              ...test,
              DateOrdered: test.DateOrdered ? new Date(test.DateOrdered) : undefined,
              ProgramStartDate: test.ProgramStartDate ? new Date(test.ProgramStartDate) : undefined,
              ProgramEndDate: test.ProgramEndDate ? new Date(test.ProgramEndDate) : undefined,
            }))

            setGridData({
              ...result,
              Records: parsedRecords || [],
              Pages: result?.Pages ?? 0,
              Total: result?.Total ?? 0,
            })
          }

          handleAPIError(error)
        },
        (error) => handleAPIError(error, () => setLoadingTests(false))
      )
    return () => subscription.unsubscribe()
  }, [page$])

  const handleSelectTest = (testId: string | undefined) => {
    const selected = gridData.Records.find((t) => t.TestDetailID === testId)
    onSelectTest(selected?.TestDetailID === selectedTest?.TestDetailID ? undefined : selected)
  }

  const handleChangePage = (page: number) => {
    handleSelectTest(undefined)
    page$.next({ ...page$.value, PageNumber: page })
  }

  return (
    <>
      <Row>
        <Col>{showTitle && <h5 className="text-body">Pending Drug Tests</h5>}</Col>
      </Row>
      <LoadingTableWrapper loading={loadingTests} variant="light" withHeader>
        <table className="table table-bordered">
          <thead className="thead-light">
            <tr>
              <th className="text-center col-1" aria-label="..." />
              <th className="text-nowrap">Reason</th>
              <th className="text-nowrap col-2">PoolID</th>
              <th className="text-nowrap col-2">Regulated</th>
              <th className="text-nowrap">Type</th>
              <th className="text-nowrap col-2">Created</th>
            </tr>
          </thead>
          <tbody className="bg-light">
            <LoadingTableBodyWrapper
              loading={loadingTests}
              recordsCount={gridData.Records.length}
              columnsCount={6}
              noDataText="No Tests Found"
            >
              {gridData.Records.map((test: PendingDrugTest) => (
                <PendingDrugTestRow
                  key={`pdt_${test.TestDetailID}`}
                  test={test}
                  highlighted={importTest.Reason === test.ReasonDescription}
                  hideCheckbox={test.ReasonDescription === "Random" && importTest.Reason !== test.ReasonDescription}
                  selected={selectedTest?.TestDetailID === test.TestDetailID}
                  onSelect={handleSelectTest}
                />
              ))}
            </LoadingTableBodyWrapper>
          </tbody>
        </table>
      </LoadingTableWrapper>
      {gridData.Records.length > 0 && (
        <Row>
          <Col sm={2}>Total records: {gridData.Total}</Col>
          <Col>
            <TablePagination
              pageNumber={page$.value.PageNumber}
              pages={gridData.Pages}
              onChange={handleChangePage}
              className="mb-0"
            />
          </Col>
        </Row>
      )}
    </>
  )
}

export default PendingDrugTests
